import React, { Suspense, useEffect } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import Loading from './components/Loading'
import RouteSkeleton from './components/RouteSkeleton'
import { loadPage } from './utils/loadPage'
import { Toaster } from './components/ui/toaster'
import { AppFallbackSEO } from './components/seo/AppFallbackSEO'

// Public marketing + article routes: eager on SSR, lazy in browser
const Index = loadPage(() => import('./pages/Index'), 'Index')
const Articles = loadPage(() => import('./pages/Articles'), 'Articles')
const ArticleDetail = loadPage(() => import('./pages/ArticleDetail'), 'ArticleDetail')
const Pricing = loadPage(() => import('./pages/Pricing'), 'Pricing')
const About = loadPage(() => import('./pages/AboutUs'), 'AboutUs')
const GetStarted = loadPage(() => import('./pages/GetStarted'), 'GetStarted')
const Founders = loadPage(() => import('./pages/Founders'), 'Founders')
const JoeyPlus = loadPage(() => import('./pages/JoeyPlus'), 'JoeyPlus')
const JoeyBasic = loadPage(() => import('./pages/JoeyBasic'), 'JoeyBasic')
const ContactUs = loadPage(() => import('./pages/ContactUs'), 'ContactUs')
const Terms = loadPage(() => import('./pages/Terms'), 'Terms')
const Privacy = loadPage(() => import('./pages/Privacy'), 'Privacy')
const FAQs = loadPage(() => import('./pages/FAQs'), 'FAQs')
const Security = loadPage(() => import('./pages/Security'), 'Security')
const IPhoneMonitoring = loadPage(() => import('./pages/IPhoneMonitoring'), 'IPhoneMonitoring')
const IPhoneMonitoringParentalControls = loadPage(
  () => import('./pages/IPhoneMonitoringParentalControls'),
  'IPhoneMonitoringParentalControls',
)
const IPhoneMonitoringSnapchat = loadPage(() => import('./pages/IPhoneMonitoringSnapchat'), 'IPhoneMonitoringSnapchat')
const IPhoneMonitoringYouTubeGoogle = loadPage(
  () => import('./pages/IPhoneMonitoringYouTubeGoogle'),
  'IPhoneMonitoringYouTubeGoogle',
)
const IPhoneMonitoringJoeyDesktop = loadPage(
  () => import('./pages/IPhoneMonitoringJoeyDesktop'),
  'IPhoneMonitoringJoeyDesktop',
)
const HowsMyChildReport = loadPage(() => import('./pages/HowsMyChildReport'), 'HowsMyChildReport')
const SmartAlerts = loadPage(() => import('./pages/SmartAlerts'), 'SmartAlerts')
const ContactVerification = loadPage(() => import('./pages/ContactVerification'), 'ContactVerification')
const Analytics = loadPage(() => import('./pages/Analytics'), 'Analytics')
const IMessageMonitoringParents = loadPage(() => import('./pages/IMessageMonitoringParents'), 'IMessageMonitoringParents')
const SmartAlertSuspiciousContacts = loadPage(
  () => import('./pages/SmartAlertSuspiciousContacts'),
  'SmartAlertSuspiciousContacts',
)
const SmartAlertBullyingHarassment = loadPage(
  () => import('./pages/SmartAlertBullyingHarassment'),
  'SmartAlertBullyingHarassment',
)
const SmartAlertConcerningLanguage = loadPage(
  () => import('./pages/SmartAlertConcerningLanguage'),
  'SmartAlertConcerningLanguage',
)
const SmartAlertEmotionalDistress = loadPage(
  () => import('./pages/SmartAlertEmotionalDistress'),
  'SmartAlertEmotionalDistress',
)
const SmartAlertMoneyGifts = loadPage(() => import('./pages/SmartAlertMoneyGifts'), 'SmartAlertMoneyGifts')
const SmartAlertPersonalInformation = loadPage(
  () => import('./pages/SmartAlertPersonalInformation'),
  'SmartAlertPersonalInformation',
)
const SmartAlertDrugsAlcohol = loadPage(() => import('./pages/SmartAlertDrugsAlcohol'), 'SmartAlertDrugsAlcohol')
// Removed deprecated/retired sub-pages for iPhone Monitoring (Snapchat, YouTube/Google, Parental Controls)
// Internal-only / fallback routes: still eager for SSR safety, lazy in browser
const NotFound = loadPage(() => import('./pages/NotFound'), 'NotFound')

export default function App() {
  const Redirect: React.FC<{ to: string }> = ({ to }) => {
    const navigate = useNavigate()
    useEffect(() => {
      navigate(to, { replace: true })
    }, [to, navigate])
    return null
  }
  return (
    <React.Suspense fallback={<RouteSkeleton />}>
      <AppFallbackSEO />
      <Routes>
        {/* Core routes */}
        <Route
          path="/"
          element={
            <Suspense fallback={<Loading />}>
              <Index />
            </Suspense>
          }
        />
        <Route
          path="/articles"
          element={
            <Suspense fallback={<Loading />}>
              <Articles />
            </Suspense>
          }
        />
        <Route
          path="/articles/:id/:slug?"
          element={
            <Suspense fallback={<Loading />}>
              <ArticleDetail />
            </Suspense>
          }
        />
        {/* Optional: Support legacy slug-only URLs and redirect in-component to canonical */}
        <Route
          path="/articles/:slug"
          element={
            <Suspense fallback={<Loading />}>
              <ArticleDetail />
            </Suspense>
          }
        />
        <Route path="/howsmychild" element={<Redirect to="/features/hows-my-child-report" />} />
        <Route path="/hows-my-child" element={<Redirect to="/features/hows-my-child-report" />} />
        <Route
          path="/pricing"
          element={
            <Suspense fallback={<Loading />}>
              <Pricing />
            </Suspense>
          }
        />
        <Route path="/about" element={<Redirect to="/about-us" />} />
        {/* Preferred slug used in nav/footer */}
        <Route
          path="/about-us"
          element={
            <Suspense fallback={<Loading />}>
              <About />
            </Suspense>
          }
        />

        {/* Solutions/Marketing pages used by hero/nav/footer */}
        <Route
          path="/imessage-monitoring-for-parents"
          element={
            <Suspense fallback={<Loading />}>
              <IMessageMonitoringParents />
            </Suspense>
          }
        />
        <Route path="/bullying-detection" element={<Redirect to="/features/smart-alerts/bullying-harassment" />} />
        <Route
          path="/get-started"
          element={
            <Suspense fallback={<Loading />}>
              <GetStarted />
            </Suspense>
          }
        />

        <Route
          path="/founders"
          element={
            <Suspense fallback={<RouteSkeleton kind="page" />}>
              <Founders />
            </Suspense>
          }
        />
        <Route
          path="/plus-coming-soon"
          element={
            <Suspense fallback={<RouteSkeleton kind="page" />}>
              <JoeyPlus />
            </Suspense>
          }
        />
        <Route
          path="/plans/basic"
          element={
            <Suspense fallback={<RouteSkeleton kind="page" />}>
              <JoeyBasic />
            </Suspense>
          }
        />
        <Route
          path="/contact"
          element={
            <Suspense fallback={<RouteSkeleton kind="form" />}>
              <ContactUs />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<RouteSkeleton kind="text" />}>
              <Terms />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<RouteSkeleton kind="text" />}>
              <Privacy />
            </Suspense>
          }
        />
        <Route
          path="/faqs"
          element={
            <Suspense fallback={<RouteSkeleton kind="list" />}>
              <FAQs />
            </Suspense>
          }
        />
        <Route
          path="/security"
          element={
            <Suspense fallback={<RouteSkeleton kind="text" />}>
              <Security />
            </Suspense>
          }
        />

        {/* Feature pages */}
        <Route
          path="/features/iphone-monitoring"
          element={
            <Suspense fallback={<Loading />}>
              <IPhoneMonitoring />
            </Suspense>
          }
        />
        <Route
          path="/features/iphone-monitoring/parental-controls"
          element={
            <Suspense fallback={<Loading />}>
              <IPhoneMonitoringParentalControls />
            </Suspense>
          }
        />
        <Route
          path="/features/iphone-monitoring/snapchat"
          element={
            <Suspense fallback={<Loading />}>
              <IPhoneMonitoringSnapchat />
            </Suspense>
          }
        />
        <Route
          path="/features/iphone-monitoring/youtube-google"
          element={
            <Suspense fallback={<Loading />}>
              <IPhoneMonitoringYouTubeGoogle />
            </Suspense>
          }
        />
        <Route
          path="/features/iphone-monitoring/joey-desktop"
          element={
            <Suspense fallback={<Loading />}>
              <IPhoneMonitoringJoeyDesktop />
            </Suspense>
          }
        />
        <Route
          path="/features/hows-my-child-report"
          element={
            <Suspense fallback={<Loading />}>
              <HowsMyChildReport />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlerts />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/suspicious-contacts"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertSuspiciousContacts />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/concerning-language-hate-speech"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertConcerningLanguage />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/emotional-distress"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertEmotionalDistress />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/money-and-gifts"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertMoneyGifts />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/personal-information"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertPersonalInformation />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/drugs-and-alcohol"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertDrugsAlcohol />
            </Suspense>
          }
        />
        <Route
          path="/features/smart-alerts/bullying-harassment"
          element={
            <Suspense fallback={<Loading />}>
              <SmartAlertBullyingHarassment />
            </Suspense>
          }
        />
        <Route
          path="/features/contact-verification"
          element={
            <Suspense fallback={<Loading />}>
              <ContactVerification />
            </Suspense>
          }
        />
        <Route
          path="/features/analytics"
          element={
            <Suspense fallback={<Loading />}>
              <Analytics />
            </Suspense>
          }
        />

        {/* Fallback */}
        <Route
          path="*"
          element={
            <Suspense fallback={<Loading />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
      <Toaster />
    </React.Suspense>
  )
}
